import type { ReactElement } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { GiftCardBalance as GiftCardBalanceSchema } from '@amplience/content-types/typings/c-gift-card-balance';
import type { GiftCardBalanceLayoutSlot } from '@amplience/content-types/typings/s-gift-card-balance-layout';
import { Locale, MasterLayout, Meta, getHierarchyData, getSlotLayout } from '@storefront/lib-global';
import { CmsSlotsProvider } from '@storefront/lib-global/context/cmsSlotsContext';
import { CmsSlot } from '@storefront/lib-global/globalComponents/cmsSlot';
import { getCmsSlotsDataForApplicationPages } from '@storefront/lib-global/services/amplience/getCmsSlotsData';
import { CMS_SLOTS_DELIVERY_KEYS } from '@storefront/lib-global/utils/amplience/constants';
import { setCustomCacheHeader } from '@storefront/lib-global/utils/setCustomCacheHeader';
import { CheckGiftCardBalance, GiftCardBalanceBreadcrumb } from '@storefront/lib-products';

enum SlotLayoutType {
  GIFT_CARD_BALANCE = 'https://clarks.com/slot/s-gift-card-balance-layout.json',
}

export default function GiftCardBalance({
  giftCardBalanceLayout,
  breadcrumbData,
  hostName,
  locale,
  cmsSlotsData,
}: InferGetServerSidePropsType<typeof getServerSideProps>): ReactElement {
  const { t } = useTranslation(['lib-global-common']);
  const metaTitle = `Clarks ${
    locale === Locale.EN_US || locale === Locale.EN_CA ? t('gift.card.balance.title') : `| ${t('gift.card.balance')}`
  }`;

  return (
    <CmsSlotsProvider cmsSlotsData={cmsSlotsData}>
      <MasterLayout>
        <Meta metaTitle={metaTitle} host={hostName} isDynamicContent />
        <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.GIFT_CARD_BALANCE_TOP_BANNER} />
        <GiftCardBalanceBreadcrumb breadcrumbData={breadcrumbData} />
        <CheckGiftCardBalance {...(giftCardBalanceLayout?.giftCardBalance as unknown as GiftCardBalanceSchema)} />
      </MasterLayout>
    </CmsSlotsProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ locale, res, req }) => {
  const giftCardBalanceLayout = (await getSlotLayout(
    locale as string,
    SlotLayoutType.GIFT_CARD_BALANCE,
  )) as GiftCardBalanceLayoutSlot;
  const breadcrumbData =
    (await getHierarchyData({
      id: giftCardBalanceLayout?.breadcrumb?._meta?.deliveryId as string,
      locale: locale as string,
      key: 'default',
    })) ?? null;

  const cmsSlotsProps = {
    locale: locale || Locale.DEFAULT_LOCALE,
    slug: ['giftcardbalance'],
    deliveryKeys: [CMS_SLOTS_DELIVERY_KEYS.GIFT_CARD_BALANCE_TOP_BANNER],
  };
  const cmsSlotsData = await getCmsSlotsDataForApplicationPages(cmsSlotsProps);

  // cache for 10 minutes, and show expired cache for maximum of two minutes
  setCustomCacheHeader(res, { maxAge: 600, staleWhileRevalidate: 120 });

  return {
    props: {
      ...(await serverSideTranslations(locale ?? 'en', [
        'lib-global-common',
        'lib-products-common',
        'lib-global-countries-common',
      ])),
      giftCardBalanceLayout,
      breadcrumbData,
      hostName: req.headers.host,
      locale,
      cmsSlotsData,
    },
  };
};
